const textValue = (value) => String(value ?? '').trim();

export function getFboTruckItemName(item) {
  return textValue(item?.article || item?.display_name || item?.name) || '—';
}

export function filterFboTruckItems(items, query) {
  const needle = textValue(query).toLocaleLowerCase('ru');
  if (!needle) return [...(items || [])];

  return (items || []).filter((item) => [
    item?.article,
    item?.display_name,
    item?.name,
    item?.nm_id,
    item?.tech_size,
    item?.barcode,
  ].some((value) => textValue(value).toLocaleLowerCase('ru').includes(needle)));
}

function compareText(left, right) {
  return textValue(left).localeCompare(textValue(right), 'ru', {
    numeric: true,
    sensitivity: 'base',
  });
}

function compareTextEmptyLast(left, right) {
  const leftText = textValue(left);
  const rightText = textValue(right);
  if (!leftText || !rightText) {
    if (!leftText && !rightText) return 0;
    return leftText ? -1 : 1;
  }
  return compareText(leftText, rightText);
}

function compareNumber(left, right) {
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  const leftValid = Number.isFinite(leftNumber);
  const rightValid = Number.isFinite(rightNumber);
  if (leftValid && rightValid) return leftNumber - rightNumber;
  if (leftValid) return -1;
  if (rightValid) return 1;
  return compareText(left, right);
}

export function sortFboTruckItemsByArticle(items) {
  return (items || [])
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftArticle = textValue(left.item?.article)
        || textValue(left.item?.display_name)
        || textValue(left.item?.name);
      const rightArticle = textValue(right.item?.article)
        || textValue(right.item?.display_name)
        || textValue(right.item?.name);
      const article = compareTextEmptyLast(leftArticle, rightArticle);
      if (article) return article;

      const productKey = compareTextEmptyLast(
        textValue(left.item?.nm_id) || textValue(left.item?.product_key),
        textValue(right.item?.nm_id) || textValue(right.item?.product_key),
      );
      if (productKey) return productKey;
      const barcode = compareTextEmptyLast(left.item?.barcode, right.item?.barcode);
      if (barcode) return barcode;
      return left.index - right.index;
    })
    .map(({ item }) => item);
}

export function sortFboTruckItems(items, orderBy = 'product', order = 'asc') {
  const direction = order === 'desc' ? -1 : 1;
  const getters = {
    product: getFboTruckItemName,
    barcode: (item) => item?.barcode,
    qty: (item) => item?.qty ?? item?.quantity ?? item?.to_supply,
    units_per_pallet: (item) => item?.units_per_pallet,
    pallets: (item) => item?.pallets,
  };
  const numericFields = new Set(['qty', 'units_per_pallet', 'pallets']);
  const getter = getters[orderBy] || getters.product;

  return (items || [])
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const primary = numericFields.has(orderBy)
        ? compareNumber(getter(left.item), getter(right.item))
        : compareText(getter(left.item), getter(right.item));
      if (primary) return primary * direction;

      const nmId = compareText(left.item?.nm_id, right.item?.nm_id);
      if (nmId) return nmId;
      const barcode = compareText(left.item?.barcode, right.item?.barcode);
      if (barcode) return barcode;
      return left.index - right.index;
    })
    .map(({ item }) => item);
}

export function prepareFboTruckItems(items, { query = '', orderBy = 'product', order = 'asc' } = {}) {
  return sortFboTruckItems(filterFboTruckItems(items, query), orderBy, order);
}
